import { node, number, oneOfType, string } from "prop-types";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";

import FnrLoadingBricks from "UI/FnrLoadingBricks2";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap";
import { opacityMotion } from "UI/motionStyles";

import useUrlParams from "Utilities/Hooks/useUrlParams";
import { addUrlParam, removeUrlParams } from "Utilities/urlParamTools";

import { useGetHouseholdDetail } from "./API/queries";
import { useHouseholdData } from "./API/queryHooks";
import Dependents from "./components/Dependents";
import Hohs from "./components/Hohs";
import HouseholdNameInput from "./components/HouseholdNameInput";

const HouseholdProfileContext = createContext();

/**
 * This a copy of the global is used lower down so that we can leverage prop drilling for smooth animations.
 * @returns {HouseholdProfileContext}
 */
const useHouseholdProfileContext = () =>
  useContext(HouseholdProfileContext) || {};

const HouseholdProfileContents = () => {
  const value = useHouseholdProfileContext();
  const { isLoading } = useGetHouseholdDetail();
  const { householdID } = value;

  useEffect(() => {
    if (householdID) {
      addUrlParam("householdID", householdID);
    }
    return () => removeUrlParams("householdID");
  }, [householdID]);

  return (
    <AnimatePresence>
      <motion.div key={householdID} {...opacityMotion} exit={undefined}>
        <>
          <FnrLoadingBricks color="grey" size={24} loading={isLoading} />
          <FnrVerticalGap data-cy="Household_Profile" gap={isLoading ? 0 : 16}>
            {/* HouseholdNameInput is another module and needs the householdID drilled down. */}
            <HouseholdNameInput householdID={householdID} isDark />
            <Hohs />
            <Dependents />
            {/* HouseholdProfileInfo is another module and needs the householdID drilled down. */}
          </FnrVerticalGap>
        </>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * @param {object} param0
 * @param {number} param0.householdID - the householdID being interacted with.
 */
const HouseholdProfile = ({ householdID }) => (
  <HouseholdProfileProvider householdID={householdID}>
    <HouseholdProfileContents />
  </HouseholdProfileProvider>
);

HouseholdProfile.propTypes = { householdID: oneOfType([string, number]) };

const HouseholdProfileProvider = ({
  householdID: householdIDArg,
  children,
}) => {
  const [primaryNetworkSetError, setPrimaryNetworkSetError] = useState("");
  const { householdID: urlHouseholdID } = useUrlParams();
  const { householdID: dataHouseholdID } = useHouseholdData();
  const [errorMessage, setErrorMessage] = useState();

  const householdID = useMemo(() => {
    return Number(householdIDArg || urlHouseholdID || dataHouseholdID);
  }, [dataHouseholdID, householdIDArg, urlHouseholdID]);

  return (
    <HouseholdProfileContext.Provider
      value={{
        householdID,
        primaryNetworkSetError,
        setPrimaryNetworkSetError,
        errorMessage,
        setErrorMessage,
      }}
    >
      {children}
    </HouseholdProfileContext.Provider>
  );
};

HouseholdProfileProvider.propTypes = {
  householdID: oneOfType([string, number]),
  children: node,
};

export { useHouseholdProfileContext, HouseholdProfileProvider };

// HouseholdProfile was split apart to make way for a different disign, but there is no reason to remove this file.
// This is exported to the drawer component where it is no longer called. And that's fine.
export default HouseholdProfile;
